import { useState } from 'react';

/**
 * The interactive controls for a crawler the viewer owns. Pure UI: it collects
 * a number and calls back to the parent, which runs the action service (real
 * rules engine) and persists. No game math here.
 *
 * `note` surfaces the rules engine's feedback — e.g. "3 damage ignored (sub-slot)"
 * or "Not enough Mana" — so the table sees why a value did or didn't change.
 */
export interface SheetActionHandlers {
  onDamage: (amount: number) => Promise<string | void>;
  onHeal: (slots: number) => Promise<string | void>;
  onSpendMana: (cost: number) => Promise<string | void>;
  onRestoreMana: (amount: number) => Promise<string | void>;
  onRename: (name: string) => Promise<void>;
  onEdit?: (edits: { level?: number; notes?: string }) => Promise<string | void>;
}

export function SheetActions(
  { name, level, notes, handlers, disabled = false }:
  { name: string; level: number; notes: string; handlers: SheetActionHandlers; disabled?: boolean },
) {
  const [amount, setAmount] = useState(1);
  const [damage, setDamage] = useState('');
  const [heal, setHeal] = useState('');
  const [healthBusy, setHealthBusy] = useState(false);
  const [note, setNote] = useState<string>('');
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const [editingFields, setEditingFields] = useState(false);
  const [dLevel, setDLevel] = useState(level);
  const [dNotes, setDNotes] = useState(notes);

  async function run(fn: (n: number) => Promise<string | void>) {
    const msg = await fn(amount);
    setNote(typeof msg === 'string' ? msg : '');
  }

  function healthAmount(text: string): number | null {
    const n = Number(text); return text.trim() && Number.isSafeInteger(n) && n >= 0 ? n : null;
  }
  async function changeHealth(kind: 'damage' | 'heal') {
    if (healthBusy || disabled) return;
    const n = healthAmount(kind === 'damage' ? damage : heal); if (n === null) return;
    setHealthBusy(true);
    try { const result = await (kind === 'damage' ? handlers.onDamage(n) : handlers.onHeal(n)); setNote(typeof result === 'string' ? result : ''); if (typeof result === 'string') { if (kind === 'damage') setDamage(''); else setHeal(''); } }
    finally { setHealthBusy(false); }
  }
  return (
    <section className="dcc-actions" aria-label="actions">
      <div className="dcc-action-rename">
        {editingName ? (
          <>
            <input
              className="dcc-name-input"
              value={draftName}
              onChange={(e: { target: { value: string } }) => setDraftName(e.target.value)}
              aria-label="crawler name"
            />
            <button onClick={async () => { await handlers.onRename(draftName.trim() || name); setEditingName(false); }}>
              Save
            </button>
            <button onClick={() => { setDraftName(name); setEditingName(false); }}>Cancel</button>
          </>
        ) : (
          <button className="dcc-rename-btn" onClick={() => { setDraftName(name); setEditingName(true); }}>
            Rename
          </button>
        )}
      </div>

      {handlers.onEdit ? (
        <div className="dcc-action-edit">
          {editingFields ? (
            <div className="dcc-edit-form">
              <label>Level
                <input type="number" min={1} value={dLevel}
                  onChange={(e: { target: { value: string } }) => setDLevel(Math.max(1, Number(e.target.value) || 1))} />
              </label>
              <label className="dcc-edit-notes">Notes
                <textarea rows={2} value={dNotes}
                  onChange={(e: { target: { value: string } }) => setDNotes(e.target.value)} />
              </label>
              <div className="dcc-action-buttons">
                <button onClick={async () => {
                  const msg = await handlers.onEdit?.({ level: dLevel, notes: dNotes });
                  setNote(typeof msg === 'string' ? msg : 'Saved.');
                  setEditingFields(false);
                }}>Save changes</button>
                <button onClick={() => { setDLevel(level); setDNotes(notes); setEditingFields(false); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button className="dcc-rename-btn" onClick={() => { setDLevel(level); setDNotes(notes); setEditingFields(true); }}>
              Edit level / notes
            </button>
          )}
        </div>
      ) : null}

      <div className="dcc-health-manager" aria-label="Adjust Health">
        <div className="dcc-health-damage"><label className="dcc-creator-field">Damage after mitigation<input type="number" min={0} step={1} value={damage} aria-label="damage amount" disabled={healthBusy || disabled} onChange={e => setDamage(e.target.value)} /></label><button className="dcc-danger" disabled={healthBusy || disabled || healthAmount(damage) === null} onClick={() => void changeHealth('damage')}>Damage</button></div>
        <div className="dcc-health-heal"><label className="dcc-creator-field">Slots to heal<input type="number" min={0} step={1} value={heal} aria-label="slots to heal" disabled={healthBusy || disabled} onChange={e => setHeal(e.target.value)} /></label><button disabled={healthBusy || disabled || healthAmount(heal) === null} onClick={() => void changeHealth('heal')}>Heal slots</button></div>
      </div>
      <p className="dcc-creator-help">Damage is an amount, after the GM resolves DR and resistance. Heal slots restores boxes, up to your maximum.</p>
      <div className="dcc-action-amount">
        <label>
          Mana amount
          <input
            type="number"
            min={0}
            value={amount}
            onChange={(e: { target: { value: string } }) => setAmount(Math.max(0, Number(e.target.value) || 0))}
            aria-label="action amount"
          />
        </label>
      </div>

      <div className="dcc-action-buttons">
        <button onClick={() => run(handlers.onSpendMana)}>Spend Mana</button>
        <button onClick={() => run(handlers.onRestoreMana)}>Restore Mana</button>
      </div>

      {note ? <p className="dcc-action-note" role="status">{note}</p> : null}
    </section>
  );
}
